import { DataTable } from "@bcc/components/data-table/data-table";
import { ProductSyncModal } from "@bcc/components/products/product-sync-modal";
import { filterLocalisedProductConfigGrinds } from "@bcc/components/shopify-import/utils/filter-localised-product-config-grinds";
import { useToast } from "@bcc/hooks/use-toast";
import type { DataTableFilter, DataTableRow } from "@client-shared";
import { useConfigState } from "@client-shared/providers/config";
import { useInstallationsState } from "@client-shared/providers/installations";
import { useProductsState } from "@client-shared/providers/products";
import { useVendorContext } from "@client-shared/providers/vendor";
import type { Product, ProductConnection } from "@shared/types";
import type { SortingState } from "@tanstack/react-table";
import * as React from "react";
import { useCallback, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { ConfigureProductModal } from "../configure-product-modal";
import { ProductImageLightbox } from "../product-image-lightbox";
import { createColumns, getColumnLabelKey } from "./columns";
import { DisconnectProductDialog } from "./disconnect-product-dialog";
import {
  buildGrindLookup,
  buildRequestsBySkuMap,
  buildRowSearchIndex,
  createProductsGlobalFilterFn,
  findProductByIdFromVariants,
  getPendingApprovalFlags,
  groupVariantsByVisibility,
  setProductsTableTestId,
} from "./products-table.utils";
import type { ProductRowPopoverType, VariantWithProductData } from "./types";

// Default initial sort: name ascending
const DEFAULT_SORTING: SortingState = [{ id: "name", desc: false }];

interface StatusFilterValue {
  key: "published" | "notVisible" | "outOfStock";
  value: "Visible for Sale" | "Not Visible" | "Out of Stock";
}

// One chip per displayed status. "Visible but not for Sale" deliberately has
// no chip of its own: it renders as "Out of Stock", so the Out of Stock chip
// matches it too (see `resolveDisplayedWebStatus` in columns.tsx).
const STATUS_FILTER_VALUES = [
  { key: "published", value: "Visible for Sale" },
  { key: "notVisible", value: "Not Visible" },
  { key: "outOfStock", value: "Out of Stock" },
] as const satisfies readonly StatusFilterValue[];

export interface ProductsTableProps {
  data: VariantWithProductData[];
  testId?: string;
  isLoading?: boolean;
  globalFilter?: string;
  handleReload?: () => void;
  allowPimVariantIdAsSkuCode?: boolean;
  // ShipStation roasters don't get the per-row Actions column (disconnect/map product).
  isShipstationRoaster?: boolean;
}

export const ProductsTable = React.memo(function ProductsTable({
  data,
  testId,
  isLoading = false,
  globalFilter,
  handleReload,
  allowPimVariantIdAsSkuCode = false,
  isShipstationRoaster = false,
}: ProductsTableProps) {
  const [sorting, setSorting] = useState<SortingState>(DEFAULT_SORTING);
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [selectedProductId, setSelectedProductId] = useState<string | number | null>(null);
  const [configureModalOpen, setConfigureModalOpen] = useState(false);
  const [configuringProductId, setConfiguringProductId] = useState<string | number | null>(null);
  const [openRowPopover, setOpenRowPopover] = useState<{
    type: ProductRowPopoverType;
    variantId: string;
  } | null>(null);

  // Manual expansion state - track which products are expanded
  const [expandedProductIds, setExpandedProductIds] = useState<Set<string>>(new Set());

  const [productsState, , productsProviderMethods] = useProductsState();
  const { toast } = useToast();
  const [disconnectVariantId, setDisconnectVariantId] = useState<string | null>(null);
  const [isDisconnecting, setIsDisconnecting] = useState(false);
  const [mapProductVariantId, setMapProductVariantId] = useState<string | null>(null);
  const [{ currentVendor }] = useVendorContext();
  const [{ installations }] = useInstallationsState();

  // Cross-border "Ship to" column visibility — driven by the vendor's
  // `properties.shipTo` (NL/DE). Mirrors the orders table gate so the column
  // only appears for cross-border vendors.
  const showShipToColumn = (currentVendor?.properties?.shipTo?.length ?? 0) > 1;

  // itself is still optional here (many hooks below read it — an early
  // `if (!currentVendor) return` would call those hooks conditionally across
  // renders, violating the Rules of Hooks). `undefined` (not `""`) is the
  // correct not-yet-loaded signal; the status filter/sort and columns.tsx
  // handle it as "no country" rather than a fabricated country match.
  const currentCountryCode = currentVendor?.countryCode;

  const activeInstallation = useMemo(() => {
    if (!installations || installations.size === 0) return null;
    for (const [, inst] of installations) {
      if (inst.state === "ACTIVE") return inst;
    }
    return null;
  }, [installations]);

  // `variant.id` can be in one of two namespaces:
  // - PIM SKU: under Standard PIM-first, and under SS for PIM-only rows.
  // - Shopify `targetVariantId`: under SS for Salesforce-sourced rows
  //   (see `mapSalesforceSkuImportVariant` in shared/src/lib/sanitize).
  // `activeConnectionsByVariant` indexes ACTIVE connections for the per-row
  // "Disconnect" action. SKU-code lookups include both ACTIVE and PAUSED
  // connections because disconnected rows still need to allow status, price,
  // and inventory edits against their PIM SKU. `pausedVariantIds` tracks
  // PAUSED connections so those rows show "Map product" and are greyed via
  // `tone: "subdued"`. A single PIM SKU can have multiple active product
  // connections (one per grind), so the active index stores arrays and
  // disconnect fans out over every connection backing the row. ACTIVE takes
  // precedence: any id present in both sets (e.g. during a state transition)
  // is removed from pausedSet after the loop.
  const { variantIdToSkuCode, connectedPimSkus, activeConnectionsByVariant, pausedVariantIds } = React.useMemo(() => {
    const targetToSource = new Map<string, string>();
    const pimSkus = new Set<string>();
    const activeMap = new Map<string, ProductConnection[]>();
    const pausedSet = new Set<string>();
    const addActiveConnection = (key: string | undefined, connection: ProductConnection) => {
      if (!key) return;
      const existing = activeMap.get(key) ?? [];
      if (existing.some((entry) => entry.id === connection.id)) {
        return;
      }
      activeMap.set(key, [...existing, connection]);
    };
    productsState?.connections?.forEach((connection) => {
      if (connection.state !== "ACTIVE" && connection.state !== "PAUSED") {
        return;
      }
      if (connection.state === "PAUSED") {
        if (connection.targetVariantId) {
          pausedSet.add(connection.targetVariantId);
        }
        if (connection.sourceObjectId) pausedSet.add(connection.sourceObjectId);
        if (connection.sourceObjectId) {
          pimSkus.add(connection.sourceObjectId);
        }
        if (connection.targetVariantId && connection.sourceObjectId) {
          targetToSource.set(connection.targetVariantId, connection.sourceObjectId);
        }
        return;
      }
      // ACTIVE-only from here.
      addActiveConnection(connection.targetVariantId, connection);
      addActiveConnection(connection.sourceObjectId, connection);
      if (!connection.sourceObjectId) return;
      pimSkus.add(connection.sourceObjectId);
      if (connection.targetVariantId) {
        targetToSource.set(connection.targetVariantId, connection.sourceObjectId);
      }
    });
    // ACTIVE takes precedence: a variant id in both ACTIVE and PAUSED
    // connections (e.g. while the backend is transitioning state) must not
    // be greyed or show "Map product" — remove any active ids from pausedSet.
    for (const id of activeMap.keys()) {
      pausedSet.delete(id);
    }
    return {
      variantIdToSkuCode: targetToSource,
      connectedPimSkus: pimSkus,
      activeConnectionsByVariant: activeMap,
      pausedVariantIds: pausedSet,
    };
  }, [productsState?.connections]);

  const requestsBySku = React.useMemo(
    () => buildRequestsBySkuMap(productsState?.approvalRequests ?? []),
    [productsState?.approvalRequests]
  );

  const { t } = useTranslation();
  const [{ config }] = useConfigState();

  const localizedProductConfig = React.useMemo(
    () =>
      filterLocalisedProductConfigGrinds(
        config.selectedLocalisedProductConfig ?? config.defaultRegionLocalisedProductConfig,
        config.productConfig
      ),
    [config.selectedLocalisedProductConfig, config.defaultRegionLocalisedProductConfig, config.productConfig]
  );

  const statusFilters = React.useMemo<DataTableFilter[]>(
    () =>
      STATUS_FILTER_VALUES.map(({ key, value }) => ({
        label: t(`views.productsTable.status.${key}`),
        column: "status",
        value,
      })),
    [t]
  );

  // Labels the columns toggle menu with the same copy as the column headers.
  const getColumnLabel = React.useCallback((columnId: string) => t(getColumnLabelKey(columnId), { defaultValue: columnId }), [t]);

  const bagSizeFilters = React.useMemo<DataTableFilter[]>(() => {
    const bagSizes = config.productConfig?.bagSize ?? [];
    return bagSizes.map((size) => ({
      label: size,
      column: "bagSize",
      value: size,
    }));
  }, [config.productConfig?.bagSize]);

  // Precompute normalized-EN-label → localized-labels once per `t`. Re-scanning
  // the EN translation bundle inside `buildRowSearchIndex` for every row was
  // O(rows × grinds × optionCount); this makes per-row enrichment O(grinds).
  const grindLookup = React.useMemo(() => buildGrindLookup(t), [t]);

  // Resolve skuCode to the PIM SKU regardless of which namespace `variant.id`
  // is in. Shopify-id rows (SS Salesforce-sourced) translate through the
  // connection map; PIM-SKU rows (Standard PIM-first, SS PIM-only) pass
  // through when they are already connected/paused or when the caller
  // explicitly allows using the PIM variant id as the skuCode. ACTIVE and
  // PAUSED rows both receive a skuCode so disconnected rows remain editable;
  // truly unconnected rows in non-PIM-first flows get `undefined` so
  // `WeeklyLimitCellMarkup` continues to render the EmptyValue placeholder.
  // Also stamp a lowercase __searchIndex used by the custom globalFilterFn.
  const enrichedData = React.useMemo(
    () =>
      data.map((variant) => {
        const skuCode =
          variantIdToSkuCode.get(variant.id) ?? (connectedPimSkus.has(variant.id) || allowPimVariantIdAsSkuCode ? variant.id : undefined);
        // A PAUSED connection greys the row via the existing DataTable
        // `tone` support (bg-muted/50 text-muted-foreground). Preserve any
        // pre-existing tone set upstream.
        const tone = pausedVariantIds.has(variant.id) ? "subdued" : variant.tone;
        const approvalRequestsForRow = skuCode ? requestsBySku.get(skuCode) : undefined;
        const pendingApprovalFlags = getPendingApprovalFlags(approvalRequestsForRow);
        const withSku = {
          ...variant,
          skuCode,
          tone,
          approvalRequestsForRow,
          ...pendingApprovalFlags,
        };
        return {
          ...withSku,
          __searchIndex: buildRowSearchIndex(withSku, t, grindLookup, currentCountryCode),
        };
      }),
    [
      data,
      variantIdToSkuCode,
      connectedPimSkus,
      allowPimVariantIdAsSkuCode,
      pausedVariantIds,
      requestsBySku,
      t,
      grindLookup,
      currentCountryCode,
    ]
  );

  const globalFilterFn = React.useMemo(
    () => createProductsGlobalFilterFn(t, grindLookup, currentCountryCode),
    [t, grindLookup, currentCountryCode]
  );

  // Toggle expansion for a product
  const handleToggleExpansion = useCallback((productId: string) => {
    setExpandedProductIds((prev) => {
      const next = new Set(prev);
      if (next.has(productId)) {
        next.delete(productId);
      } else {
        next.add(productId);
      }
      return next;
    });
  }, []);

  // Find product data from variant data by productId (delegated to utility)
  const findProductById = useCallback(
    (productId: string | number | null) => {
      return findProductByIdFromVariants(productId, data);
    },
    [data]
  );

  const handleImageClick = useCallback((productId: string | number) => {
    setSelectedProductId(productId);
    setLightboxOpen(true);
  }, []);

  const handleConfigureDraft = useCallback((productId: string | number) => {
    setConfiguringProductId(productId);
    setConfigureModalOpen(true);
  }, []);

  const getPopoverOpen = useCallback(
    (type: ProductRowPopoverType, variantId: string) => openRowPopover?.type === type && openRowPopover.variantId === variantId,
    [openRowPopover]
  );
  const handlePopoverOpenChange = useCallback((type: ProductRowPopoverType, variantId: string, open: boolean) => {
    setOpenRowPopover(open ? { type, variantId } : null);
  }, []);

  const isDisconnectVisible = useCallback(
    (variantId: string) => (activeConnectionsByVariant.get(variantId)?.length ?? 0) > 0,
    [activeConnectionsByVariant]
  );

  const handleDisconnectRequest = useCallback((variantId: string) => {
    setDisconnectVariantId(variantId);
  }, []);

  const handleDisconnectOpenChange = useCallback(
    (open: boolean) => {
      if (!open && !isDisconnecting) {
        setDisconnectVariantId(null);
      }
    },
    [isDisconnecting]
  );

  const handleDisconnectConfirm = useCallback(async () => {
    if (!disconnectVariantId) return;
    // A row may aggregate multiple PIM variants (e.g. one (product, bagSize)
    // row carrying several grind variants). Disconnect must pause every
    // connection backing the row, not just baseVariant's, otherwise the
    // remaining grinds stay ACTIVE and the row keeps showing as connected.
    const row = data.find((rowData) => rowData.id === disconnectVariantId);
    const targetIds =
      row?.groupTargetVariantIds && row.groupTargetVariantIds.length > 0 ? row.groupTargetVariantIds : [disconnectVariantId];
    const seenConnectionIds = new Set<string>();
    const pausedConnections: ProductConnection[] = [];
    for (const targetId of targetIds) {
      const activeConnections = activeConnectionsByVariant.get(targetId) ?? [];
      for (const connection of activeConnections) {
        if (seenConnectionIds.has(connection.id)) continue;
        seenConnectionIds.add(connection.id);
        pausedConnections.push({ ...connection, state: "PAUSED" });
      }
    }
    if (pausedConnections.length === 0) return;
    setIsDisconnecting(true);
    try {
      const submitted = await productsProviderMethods?.handleSyncProductConnections?.([], pausedConnections);
      if (!submitted) {
        toast({
          variant: "destructive",
          title: t("views.productsTable.disconnect.error"),
        });
        return;
      }
      toast({
        variant: "success",
        title: t("views.productsTable.disconnect.success"),
      });
      setDisconnectVariantId(null);
      handleReload?.();
    } catch {
      toast({
        variant: "destructive",
        title: t("views.productsTable.disconnect.error"),
      });
    } finally {
      setIsDisconnecting(false);
    }
  }, [activeConnectionsByVariant, data, disconnectVariantId, handleReload, productsProviderMethods, t, toast]);

  // Map-product is visible for PAUSED rows and rows with no active connection.
  // ACTIVE rows use the "Disconnect" action instead.
  const isMapProductVisible = useCallback(
    (variantId: string) => pausedVariantIds.has(variantId) || (activeConnectionsByVariant.get(variantId)?.length ?? 0) === 0,
    [activeConnectionsByVariant, pausedVariantIds]
  );

  const handleMapProductRequest = useCallback((variantId: string) => {
    setMapProductVariantId(variantId);
  }, []);

  const handleMapProductClose = useCallback(() => {
    setMapProductVariantId(null);
  }, []);

  // Group variants by product and determine visibility (delegated to utility).
  // Disconnected rows stay visible; their PAUSED state is conveyed via the
  // `tone: "subdued"` stamped in `enrichedData` above.
  const { visibleData, expandableProducts, firstVariantIds } = React.useMemo(
    () => groupVariantsByVisibility(enrichedData, expandedProductIds),
    [enrichedData, expandedProductIds]
  );

  // Memoize columns to prevent infinite render loops. The status-cell
  // popover consumes its provider state (onUpdateSkuStatus, isUpdating,
  // vendorKey) directly, so isUpdating toggles during submit don't churn
  // this memo — which would otherwise reset per-row local state
  // (e.g. ProductThumbnail.imageError).
  const columns = React.useMemo(
    () =>
      createColumns({
        getPopoverOpen,
        onPopoverOpenChange: handlePopoverOpenChange,
        onConfigureDraft: handleConfigureDraft,
        onImageClick: handleImageClick,
        onDisconnect: handleDisconnectRequest,
        isDisconnectVisible,
        isDisconnecting,
        onMapProduct: handleMapProductRequest,
        isMapProductVisible,
        expandedProductIds,
        expandableProducts,
        firstVariantIds,
        onToggleExpansion: handleToggleExpansion,
        showShipToColumn,
        currentCountryCode,
        isShipstationRoaster,
      }),
    [
      getPopoverOpen,
      handlePopoverOpenChange,
      handleConfigureDraft,
      handleImageClick,
      handleDisconnectRequest,
      isDisconnectVisible,
      isDisconnecting,
      handleMapProductRequest,
      isMapProductVisible,
      expandedProductIds,
      expandableProducts,
      firstVariantIds,
      handleToggleExpansion,
      showShipToColumn,
      currentCountryCode,
      isShipstationRoaster,
    ]
  );

  // visibleData already satisfies DataTableRow<T> — DataTableRow only adds optional fields
  const tableData = visibleData as DataTableRow<VariantWithProductData>[];

  const selectedProduct = findProductById(selectedProductId);
  const configuringProduct = findProductById(configuringProductId);
  const disconnectConnection = disconnectVariantId ? activeConnectionsByVariant.get(disconnectVariantId)?.[0] : undefined;

  // Resolve the `Product` to pass into ProductSyncModal's single-product
  // mode. Prefer the real `Product` from the provider when it exists (the
  // standard PIM-first path); otherwise reconstruct a minimal Product from
  // the row data so orphan rows synthesised from connections (PIM product
  // has empty `variants[]`) can still open the modal.
  const mapTargetProduct = React.useMemo<Product | undefined>(() => {
    if (!mapProductVariantId) return undefined;
    const row = data.find((variant) => variant.id === mapProductVariantId);
    if (!row) return undefined;
    const productIdString = String(row.productId);
    const local = (productsState?.localProducts ?? []).find((product) => String(product.id) === productIdString);
    if (local) return local;
    const groupedVariants = data.filter((variant) => String(variant.productId) === productIdString);
    return {
      type: "product",
      id: productIdString,
      title: row.productTitle ?? "",
      variants: groupedVariants.map((variant) => ({
        type: "variant",
        id: variant.id,
        bagSize: variant.bagSize,
        title: variant.title,
        grind: variant.grind,
      })),
    };
  }, [data, mapProductVariantId, productsState?.localProducts]);

  // Ensure the synthetic orphan Product is present in the list the modal
  // searches. When `mapTargetProduct` was built from row data (not found in
  // `localProducts`), `eligibleProducts` inside ProductSync would filter to
  // an empty array because the synthetic id has no entry in the real list.
  const modalLocalProducts = React.useMemo(() => {
    const base = productsState?.localProducts ?? [];
    if (!mapTargetProduct) return base;
    const alreadyPresent = base.some((p) => String(p.id) === String(mapTargetProduct.id));
    return alreadyPresent ? base : [mapTargetProduct, ...base];
  }, [mapTargetProduct, productsState?.localProducts]);

  // Bundle all required modal dependencies into one object so the JSX only
  // needs a single truthiness check and TypeScript narrows all values at once.
  const modalContext = React.useMemo(() => {
    if (!mapProductVariantId || !mapTargetProduct || !currentVendor || !activeInstallation) {
      return null;
    }
    return {
      vendor: currentVendor,
      installation: activeInstallation,
      targetProduct: mapTargetProduct,
    };
  }, [mapProductVariantId, mapTargetProduct, currentVendor, activeInstallation]);

  return (
    <>
      <div data-testid={testId ?? setProductsTableTestId("container")}>
        <DataTable
          data={tableData}
          columns={columns}
          sorting={sorting}
          onSortingChange={setSorting}
          searchable={false}
          globalFilter={globalFilter}
          globalFilterFn={globalFilterFn}
          isLoading={isLoading}
          filters={[...statusFilters, ...bagSizeFilters]}
          enableColumnVisibility
          getColumnLabel={getColumnLabel}
          persistenceKey="products-table"
        />
      </div>

      {selectedProduct && (
        <ProductImageLightbox
          isOpen={lightboxOpen}
          onClose={() => setLightboxOpen(false)}
          imageUrl={selectedProduct.imageUrl}
          productName={selectedProduct.name}
        />
      )}

      {configuringProduct && (
        <ConfigureProductModal
          product={configuringProduct}
          open={configureModalOpen}
          onOpenChange={setConfigureModalOpen}
          onSave={() => {
            setConfigureModalOpen(false);
          }}
        />
      )}

      {disconnectConnection && (
        <DisconnectProductDialog
          open={disconnectVariantId !== null}
          onOpenChange={handleDisconnectOpenChange}
          identifier={disconnectConnection.identifier}
          onConfirm={handleDisconnectConfirm}
          isPending={isDisconnecting}
        />
      )}

      {modalContext && (
        <ProductSyncModal
          expanded={mapProductVariantId !== null}
          handleClose={handleMapProductClose}
          handleReload={handleMapProductClose}
          vendor={modalContext.vendor}
          installation={modalContext.installation}
          localProducts={modalLocalProducts}
          remoteProducts={productsState?.remoteProducts ?? []}
          connections={productsState?.connections ?? []}
          remoteFormProducts={productsState?.remoteFormProducts ?? []}
          targetProduct={modalContext.targetProduct}
          localizedProductConfig={localizedProductConfig}
        />
      )}
    </>
  );
});
